/**
 * Card and bank fields for a payment read out over the phone.
 *
 * The number never enters React state: these are UNCONTROLLED inputs with no
 * `name`, read once by `tokenize()` at submit, handed to Accept.js (which
 * returns a one-time token), and cleared at once. Nothing here is logged or
 * sent to our server; only the token goes to /api/desk/payments/phone.
 * Same contract as reader-app/src/lib/accept.ts.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { dispatch, loadAcceptJs, splitExpiry, type GatewayConfig, type Nonce } from './acceptJs'

export interface AcceptHandle {
  /** Read the inputs, tokenize, clear them. Rejects with a message fit to show. */
  tokenize: (extra: { zip?: string; fullName?: string; accountType?: string }) => Promise<Nonce>
}

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const inp = `min-h-[44px] w-full rounded-lg border border-ink-400 bg-ink-800 px-3 font-mono text-[15px] text-paper ${focus}`
const lbl = 'flex flex-col gap-1.5 text-[13px] font-semibold text-paper/70'

export const AcceptFields = forwardRef<AcceptHandle, { instrument: 'card' | 'bank'; config: GatewayConfig; disabled?: boolean }>(
  function AcceptFields({ instrument, config, disabled }, ref) {
    const num = useRef<HTMLInputElement>(null)
    const exp = useRef<HTMLInputElement>(null)
    const cvv = useRef<HTMLInputElement>(null)
    const routing = useRef<HTMLInputElement>(null)
    const acct = useRef<HTMLInputElement>(null)
    const [ready, setReady] = useState(Boolean(window.Accept))
    const [loadError, setLoadError] = useState<string | null>(null)

    useEffect(() => {
      let live = true
      loadAcceptJs(config.script_url).then(() => live && setReady(true), (e: Error) => live && setLoadError(e.message))
      return () => { live = false }
    }, [config.script_url])

    const wipe = () => {
      for (const r of [num, exp, cvv, routing, acct]) if (r.current) r.current.value = ''
    }
    useEffect(() => wipe, [])

    useImperativeHandle(ref, () => ({
      async tokenize({ zip, fullName, accountType }) {
        if (!ready) throw new Error(loadError ?? 'The card form is still loading. Try again in a moment.')
        if (instrument === 'card') {
          const e = splitExpiry(exp.current?.value ?? '')
          if (!e) throw new Error('Enter the expiry as MM/YY.')
          const cardData: Record<string, string> = {
            cardNumber: (num.current?.value ?? '').replace(/\s/g, ''), month: e.month, year: e.year,
            cardCode: (cvv.current?.value ?? '').trim(),
          }
          // Accept.js validates any key that is present, so blanks stay out.
          if (zip?.trim()) cardData.zip = zip.trim()
          if (fullName?.trim()) cardData.fullName = fullName.trim()
          const p = dispatch(config, { cardData }, 'card')
          wipe()
          return p
        }
        const bankData = {
          accountNumber: (acct.current?.value ?? '').replace(/\s/g, ''),
          routingNumber: (routing.current?.value ?? '').replace(/[\s-]/g, ''),
          nameOnAccount: (fullName ?? '').slice(0, 22),
          accountType: accountType ?? 'checking',
        }
        const p = dispatch(config, { bankData }, 'bank')
        wipe()
        return p
      },
    }), [ready, loadError, instrument, config])

    return (
      <div className="flex flex-col gap-3">
        {loadError && <p role="alert" className="text-[13px] text-radar-red">{loadError}</p>}
        {instrument === 'card' ? (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3.5">
            <label className={`${lbl} sm:col-span-2`}>Card number
              <input ref={num} className={inp} inputMode="numeric" autoComplete="off" disabled={disabled} aria-label="Card number" />
            </label>
            <label className={lbl}>Expiry
              <input ref={exp} className={inp} placeholder="MM/YY" autoComplete="off" disabled={disabled} aria-label="Expiry" />
            </label>
            <label className={lbl}>Security code
              <input ref={cvv} className={inp} inputMode="numeric" autoComplete="off" disabled={disabled} aria-label="Security code" />
            </label>
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3.5">
            <label className={lbl}>Routing number
              <input ref={routing} className={inp} inputMode="numeric" autoComplete="off" disabled={disabled} aria-label="Routing number" />
            </label>
            <label className={lbl}>Account number
              <input ref={acct} className={inp} inputMode="numeric" autoComplete="off" disabled={disabled} aria-label="Account number" />
            </label>
          </div>
        )}
      </div>
    )
  },
)
